import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RegisterData } from '../types';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, Circle, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';
import { trackEvent, ANALYTICS_EVENTS } from '../services/analytics';

interface RegisterFormProps {
  onSuccess?: () => void;
  inviteToken?: string;
  inviteCode?: string;
  initialEmail?: string;
}

interface RegisterFormData extends RegisterData {
  confirmPassword: string;
  agreedToTerms: boolean;
}

interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, inviteToken, inviteCode, initialEmail }) => {
  const { register } = useAuth();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: initialEmail || '',
    password: '',
    confirmPassword: '',
    name: '',
    agreedToTerms: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof RegisterFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');

  // Clear API error when changing steps
  useEffect(() => {
    setApiError('');
  }, [currentStep]);

  const validatePassword = (password: string): string | null => {
    // Minimum security requirements (Requirements 8.1)
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const calculatePasswordStrength = (password: string): PasswordStrength => {
    if (!password) {
      return { score: 0, label: 'No password', color: 'bg-gray-300' };
    }

    let score = 0;

    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;

    // Character variety checks
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++; // Special characters

    // Cap at 4
    score = Math.min(score, 4);

    const strengthMap: Record<number, { label: string; color: string }> = {
      0: { label: 'Very Weak', color: 'bg-error-500' },
      1: { label: 'Weak', color: 'bg-error-500' },
      2: { label: 'Fair', color: 'bg-warning-500' },
      3: { label: 'Good', color: 'bg-success-500' },
      4: { label: 'Strong', color: 'bg-success-600' },
    };

    return { score, ...strengthMap[score] };
  };

  const passwordStrength = calculatePasswordStrength(formData.password);

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof RegisterFormData, string>> = {};
    let isValid = true;

    if (step === 1) {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
        isValid = false;
      } else if (formData.name.trim().length < 2) {
        newErrors.name = 'Name must be at least 2 characters long';
        isValid = false;
      }
    } else if (step === 2) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
        isValid = false;
      }
    } else if (step === 3) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
        isValid = false;
      } else {
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
          newErrors.password = passwordError;
          isValid = false;
        }
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
        isValid = false;
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
        isValid = false;
      }
      
      if (!formData.agreedToTerms) {
        newErrors.agreedToTerms = 'You must agree to the terms';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setDirection('forward');
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setDirection('backward');
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setApiError('');
    setIsSubmitting(true);

    try {
      const { confirmPassword, agreedToTerms, ...registerData } = formData;
      
      // Store email in sessionStorage so RegisterPage can access it for verification
      sessionStorage.setItem('registerEmail', formData.email);
      
      const dataToSubmit = { ...registerData, inviteCode };
      await register(dataToSubmit, inviteToken);
      
      trackEvent(ANALYTICS_EVENTS.SIGNUP_SUCCESS);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Registration failed. Please try again.';
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof RegisterFormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const steps = [
    { number: 1, label: 'Name', description: 'What should we call you?' },
    { number: 2, label: 'Email', description: 'Your distinctive contact info.' },
    { number: 3, label: 'Password', description: 'Secure your account.' },
  ];

  return (
    <div className="w-full">
        {/* Step Indicator */}
        <div className="mb-8 border-b-2 border-gray-100 pb-4">
             <div className="flex items-center justify-between text-sm font-bold text-[#161E54] mb-2">
                 <span>Step {currentStep} of 3</span>
                 <span className="text-[#F16D34]">{steps[currentStep-1].label}</span>
             </div>
             <div className="h-1.5 w-full bg-gray-100">
                 <div 
                    className="h-full bg-[#161E54] transition-all duration-500 ease-out" 
                    style={{ width: `${(currentStep / 3) * 100}%` }}
                 />
             </div>
             <p className="mt-2 text-sm text-gray-500">{steps[currentStep-1].description}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* Name Input */}
          <div className={cn("space-y-4 transition-all duration-300", currentStep === 1 ? "block animate-fade-in-up" : "hidden")}>
             <div className="space-y-2">
               <label htmlFor="name" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-[#161E54]">Full Name</label>
               <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  disabled={isSubmitting}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  autoFocus={currentStep === 1}
                  className={cn("h-14 text-lg", errors.name && "border-red-500")}
                />
                {errors.name && <p className="text-sm font-medium text-red-500">{errors.name}</p>}
                <p className="text-sm text-muted-foreground">This will be displayed to your team members</p>
             </div>
          </div>

          {/* Step 2: Email */}
          <div className={cn("space-y-4 transition-all duration-300", currentStep === 2 ? "block animate-fade-in-up" : "hidden")}>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-[#161E54]">Email Address</label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={isSubmitting}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus={currentStep === 2}
                  className={cn("h-14 text-lg", errors.email && "border-red-500")}
                />
                {errors.email && <p className="text-sm font-medium text-red-500">{errors.email}</p>}
                <p className="text-sm text-muted-foreground">We'll use this for login and notifications</p>
              </div>
          </div>

          {/* Step 3: Password */}
          <div className={cn("space-y-6 transition-all duration-300", currentStep === 3 ? "block animate-fade-in-up" : "hidden")}>
            <div className="space-y-4">
                <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-[#161E54]">Password</label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      disabled={isSubmitting}
                      placeholder="Create a password"
                      autoComplete="new-password"
                      autoFocus={currentStep === 3}
                      className={cn("h-14 text-lg", errors.password && "border-red-500")}
                    />
                    {errors.password && <p className="text-sm font-medium text-red-500">{errors.password}</p>}
                </div>

                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full transition-all duration-300',
                            passwordStrength.color
                          )}
                          style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                        />
                      </div>
                      <Badge
                        variant={passwordStrength.score >= 3 ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {passwordStrength.label}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                       <RequirementCheck filled={formData.password.length >= 8} label="8+ chars" />
                       <RequirementCheck filled={/[A-Z]/.test(formData.password)} label="Uppercase" />
                       <RequirementCheck filled={/[a-z]/.test(formData.password)} label="Lowercase" />
                       <RequirementCheck filled={/[0-9]/.test(formData.password)} label="Number" />
                    </div>
                  </div>
                )}
            </div>

            <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-[#161E54]">Confirm Password</label>
                <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    className={cn("h-14 text-lg", errors.confirmPassword && "border-red-500")}
                />
                {errors.confirmPassword && <p className="text-sm font-medium text-red-500">{errors.confirmPassword}</p>}
            </div>

            <div className="space-y-2">
                <div className="flex items-start gap-2">
                    <input
                        id="agreedToTerms"
                        type="checkbox"
                        checked={formData.agreedToTerms}
                        onChange={(e) => setFormData(prev => ({ ...prev, agreedToTerms: e.target.checked }))}
                        disabled={isSubmitting}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#F16D34] focus:ring-[#F16D34]"
                    />
                    <label htmlFor="agreedToTerms" className="text-sm text-[#547792]">
                        I agree to the <Link to="/terms" target="_blank" className="font-bold text-[#161E54] hover:text-[#F16D34] underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="font-bold text-[#161E54] hover:text-[#F16D34] underline">Privacy Policy</Link>
                    </label>
                </div>
                {errors.agreedToTerms && <p className="text-sm font-medium text-red-500">{errors.agreedToTerms}</p>}
            </div>
          </div>

          {/* API Error Message */}
          {apiError && (
            <div className="flex items-center gap-2 p-4 rounded-lg bg-error-50 border border-error-200 text-error-600 animate-in fade-in slide-in-from-top-2 duration-200">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">{apiError}</span>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 pt-2">
              {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    className="flex-1 h-12"
                    disabled={isSubmitting}
                  >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                  </Button>
              )}
              
              {currentStep < 3 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    className="flex-1 h-12 font-bold shadow-lg shadow-primary/20"
                  >
                      Next Step
                      <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
              ) : (
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 h-12 font-bold shadow-lg shadow-primary/20 bg-[#F16D34] hover:bg-[#E05D25]"
                  >
                    {isSubmitting ? 'Creating Account...' : 'Complete Sign Up'}
                  </Button>
              )}
          </div>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-12 gap-2"
            onClick={() => {
              let url = import.meta.env.VITE_API_URL || 'http://localhost:8080';
              url = url.trim().replace(/\/+$/, '');
              if (url.endsWith('/api')) {
                url = url.slice(0, -4);
              }
              window.location.href = `${url}/api/auth/google/login`;
            }}
          >
            <svg className="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign up with Google
          </Button>
        </form>
    </div>
  );
};

const RequirementCheck = ({ filled, label }: { filled: boolean; label: string }) => (
    <div className="flex items-center gap-1.5">
        {filled ? (
            <CheckCircle2 className="h-3 w-3 text-success-500" />
        ) : (
            <Circle className="h-3 w-3 text-gray-300" />
        )}
        <span className={cn("transition-colors", filled ? 'text-success-700 font-medium' : 'text-gray-400')}>
            {label}
        </span>
    </div>
);

export default RegisterForm;
